import type { IndicatorDef, InputSpec, InputValues, OutputSpec } from './types'

/** Inputs ⇄ settings ⇄ klinecharts calcParams, plus the human/AI renderings. */

const isFinite_ = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)

function specDefault(spec: InputSpec): number {
  return spec.kind === 'bool' ? (spec.default ? 1 : 0) : spec.default
}

export function defaultInputs(def: IndicatorDef): InputValues {
  const out: InputValues = {}
  for (const spec of def.inputs) out[spec.key] = specDefault(spec)
  return out
}

/** Builtin v1 inputs are `p1..pk`, contiguous from p1; the count is per INSTANCE (klinecharts arity). */
function contiguousP(raw: Record<string, unknown>): InputValues {
  const out: InputValues = {}
  for (let i = 1; ; i++) {
    const v = raw[`p${i}`]
    if (!isFinite_(v)) break
    out[`p${i}`] = v
  }
  return out
}

function clampOne(spec: InputSpec, v: unknown): number {
  switch (spec.kind) {
    case 'int': {
      if (!isFinite_(v)) return spec.default
      let n = Math.round(v)
      if (n < spec.min) n = spec.min
      if (spec.max !== undefined && n > spec.max) n = spec.max
      return n
    }
    case 'float': {
      if (!isFinite_(v)) return spec.default
      let n = v
      if (spec.min !== undefined && n < spec.min) n = spec.min
      if (spec.max !== undefined && n > spec.max) n = spec.max
      return n
    }
    case 'select':
      return isFinite_(v) && spec.options.some((o) => o.value === v) ? v : spec.default
    case 'bool':
      if (typeof v === 'boolean') return v ? 1 : 0
      return v === 1 || v === 0 ? v : spec.default ? 1 : 0
  }
}

/** Untrusted → valid inputs: unknown keys dropped, bad/missing values → defaults, ints rounded and clamped. */
export function clampInputs(def: IndicatorDef, raw: unknown): InputValues {
  const r = raw !== null && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  if (def.builtin) return contiguousP(r)
  const out: InputValues = {}
  for (const spec of def.inputs) out[spec.key] = clampOne(spec, r[spec.key])
  return out
}

/** Legacy saved `params: number[]` → `{ p1, p2, … }` (builtin v1 migration). */
export function paramsToInputs(params: readonly unknown[]): InputValues {
  const out: InputValues = {}
  for (let i = 0; i < params.length; i++) {
    const v = params[i]
    if (!isFinite_(v)) break
    out[`p${i + 1}`] = v
  }
  return out
}

/** klinecharts calcParams: positional in schema order (builtin: the contiguous p-values). */
export function toCalcParams(def: IndicatorDef, inputs: InputValues): number[] {
  if (def.builtin) return Object.values(contiguousP(inputs))
  return def.inputs.map((spec) => (isFinite_(inputs[spec.key]) ? inputs[spec.key] : specDefault(spec)))
}

/** Positional calcParams → keyed inputs (tolerant: short/invalid arrays fall back to defaults). */
export function fromCalcParams(def: IndicatorDef, params: readonly unknown[] | undefined): InputValues {
  const p = params ?? []
  if (def.builtin) return paramsToInputs(p)
  const raw: Record<string, unknown> = {}
  def.inputs.forEach((spec, i) => {
    raw[spec.key] = p[i]
  })
  return clampInputs(def, raw)
}

function renderValue(spec: InputSpec | undefined, v: number): string {
  if (spec?.kind === 'select') return spec.options.find((o) => o.value === v)?.label ?? String(v)
  if (spec?.kind === 'bool') return v ? 'on' : 'off'
  return String(v)
}

/** Legend text: "14", "12, 26, 9", "20, SMA". */
export function summarizeInputs(def: IndicatorDef, inputs: InputValues): string {
  if (def.builtin) return Object.values(contiguousP(inputs)).join(', ')
  return def.inputs.map((spec) => renderValue(spec, inputs[spec.key] ?? specDefault(spec))).join(', ')
}

/** The TA-Lib call(s) the AI should reproduce, e.g. "RSI(close, timeperiod=14)". */
export function describeTalib(def: IndicatorDef, inputs: InputValues): string {
  const ref = def.talib
  if (!ref) return ''
  const fill = (expr: string) =>
    expr.replace(/\{(\w+)\}/g, (_, key: string) => {
      const spec = def.inputs.find((s) => s.key === key)
      const v = inputs[key] ?? (spec ? specDefault(spec) : NaN)
      return String(v)
    })
  if (typeof ref.expr === 'string') return fill(ref.expr)
  return Object.entries(ref.expr)
    .map(([key, expr]) => `${key} = ${fill(expr)}`)
    .join('; ')
}

export interface ColorSlot {
  label: string
  outputKey: string
  role: 'line' | 'dots' | 'up' | 'down'
}

/** The editable colour list: one slot per line/dots output, two (up, down) per histogram, two (bullish, bearish) per marker. */
export function colorSlotsFor(def: IndicatorDef): ColorSlot[] {
  const slots: ColorSlot[] = []
  for (const o of def.outputs) {
    if (o.plot === 'histogram') {
      slots.push({ label: `${o.label} up`, outputKey: o.key, role: 'up' })
      slots.push({ label: `${o.label} down`, outputKey: o.key, role: 'down' })
    } else if (o.plot === 'marker') {
      slots.push({ label: 'Bullish', outputKey: o.key, role: 'up' })
      slots.push({ label: 'Bearish', outputKey: o.key, role: 'down' })
    } else {
      slots.push({ label: o.label, outputKey: o.key, role: o.plot })
    }
  }
  return slots
}

function slotDefault(o: OutputSpec, role: ColorSlot['role']): string {
  return role === 'down' ? (o.downColor ?? o.color) : o.color
}

/** Default colours by colour slot (builtin: klinecharts' palette, by figure index). */
export function defaultColors(def: IndicatorDef): string[] {
  if (def.builtin) return [...DEFAULT_LINE_COLORS]
  return colorSlotsFor(def).map((s) => slotDefault(def.outputs.find((o) => o.key === s.outputKey)!, s.role))
}

/** klinecharts' own per-line palette (what builtin v1 instances use). */
export const DEFAULT_LINE_COLORS = ['#FF9600', '#935EBD', '#1677FF', '#E11D74', '#01C5C4']
